import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import axios from 'axios';
import { authAPI } from '../../api/authAPI';
import getAuraApiBaseUrl from '../../api/auraApiBase';
import { formatAuraScoreDisplay } from '../../utils/formatAuraScore';
import { decodeSearchCategory } from '../../constants/sideMenuCategories';
import SearchCategorySelect from './SearchCategorySelect';
import styled, { css } from 'styled-components';
import {
  VERTICAL_THEME,
  getVerticalThemeFromPath,
  isDarkVerticalSearchTheme,
} from '../../constants/verticalSectionTheme';

const AURA_BASE = getAuraApiBaseUrl();

const SEARCH_BAR_THEMES = {
  [VERTICAL_THEME.mint]: css`
    background-color: #a8e6cf;
    border-bottom: 1px solid #88d8a3;
  `,
  [VERTICAL_THEME.curated]: css`
    background-color: #f6f1e8;
    border-bottom: 1px solid #e4dccb;
  `,
  [VERTICAL_THEME.luxury]: css`
    background: linear-gradient(180deg, #f4efe6 0%, #ebe4d8 100%);
    border-bottom: 2px solid #c4a574;
  `,
  [VERTICAL_THEME.designers]: css`
    background: linear-gradient(180deg, #eef0ff 0%, #e4e7fb 100%);
    border-bottom: 2px solid #818cf8;
  `,
  [VERTICAL_THEME.boutiques]: css`
    background: linear-gradient(180deg, #e8f7f4 0%, #d9f0eb 100%);
    border-bottom: 2px solid #0f766e;
  `,
  [VERTICAL_THEME.wedding]: css`
    background: linear-gradient(180deg, #fdf2f8 0%, #fce7f3 100%);
    border-bottom: 2px solid #be185d;
  `,
};

const SEARCH_FG = {
  [VERTICAL_THEME.mint]: {
    text: '#1b4332',
    placeholder: 'rgba(27, 67, 50, 0.5)',
    underline: 'rgba(27, 67, 50, 0.28)',
    button: '#1b4332',
    buttonHover: '#2d6a4f',
    buttonActive: '#043927',
  },
  [VERTICAL_THEME.curated]: {
    text: '#1b4332',
    placeholder: 'rgba(27, 67, 50, 0.5)',
    underline: 'rgba(27, 67, 50, 0.28)',
    button: '#1b4332',
    buttonHover: '#2d6a4f',
    buttonActive: '#043927',
  },
  [VERTICAL_THEME.luxury]: {
    text: '#292524',
    placeholder: 'rgba(41, 37, 36, 0.5)',
    underline: 'rgba(196, 165, 116, 0.55)',
    button: '#57534e',
    buttonHover: '#292524',
    buttonActive: '#1c1917',
  },
  [VERTICAL_THEME.designers]: {
    text: '#312e81',
    placeholder: 'rgba(49, 46, 129, 0.5)',
    underline: 'rgba(129, 140, 248, 0.55)',
    button: '#4338ca',
    buttonHover: '#312e81',
    buttonActive: '#1e1b4b',
  },
  [VERTICAL_THEME.boutiques]: {
    text: '#134e4a',
    placeholder: 'rgba(19, 78, 74, 0.5)',
    underline: 'rgba(15, 118, 110, 0.45)',
    button: '#0f766e',
    buttonHover: '#134e4a',
    buttonActive: '#115e59',
  },
  [VERTICAL_THEME.wedding]: {
    text: '#9f1239',
    placeholder: 'rgba(159, 18, 57, 0.5)',
    underline: 'rgba(190, 24, 93, 0.4)',
    button: '#be185d',
    buttonHover: '#9f1239',
    buttonActive: '#831843',
  },
};

const SearchBarContainer = styled.div`
  padding: 1rem 0;
  ${(p) => SEARCH_BAR_THEMES[p.$theme] || SEARCH_BAR_THEMES.mint}

  @media (max-width: 768px) {
    padding: 0.625rem 0;
  }
`;

const SearchBarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    padding: 0 0.625rem;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
`;

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid
    ${(p) => (SEARCH_FG[p.$theme] || SEARCH_FG.mint).underline};
  border-radius: 0;
  box-shadow: none;
  padding: 0.15rem 0;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.65rem 0.5rem;
  border: none;
  background: transparent;
  color: ${(p) => (SEARCH_FG[p.$theme] || SEARCH_FG.mint).text};
  font-size: 1rem;
  outline: none;
  min-width: 0;

  &::placeholder {
    color: ${(p) => (SEARCH_FG[p.$theme] || SEARCH_FG.mint).placeholder};
  }

  @media (max-width: 768px) {
    padding: 0.55rem 0.35rem;
    font-size: 0.9rem;
  }
`;

const SearchButton = styled.button`
  background: transparent;
  border: none;
  padding: 0.5rem 0.65rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: ${(p) => (SEARCH_FG[p.$theme] || SEARCH_FG.mint).button};
  font-size: 1rem;
  flex-shrink: 0;

  &:hover {
    color: ${(p) => (SEARCH_FG[p.$theme] || SEARCH_FG.mint).buttonHover};
  }
  &:active {
    color: ${(p) => (SEARCH_FG[p.$theme] || SEARCH_FG.mint).buttonActive};
  }

  @media (max-width: 768px) {
    padding: 0.45rem 0.4rem;
    min-width: 40px;
  }
`;

/* ── user suggestion dropdown ── */

const Dropdown = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  z-index: 9999;
  max-height: 320px;
  overflow-y: auto;
`;

const DropdownItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1rem;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f5f5f5;
  }
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #a8e6cf;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  color: #043927;
  flex-shrink: 0;
  overflow: hidden;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserHandle = styled.div`
  font-size: 0.78rem;
  color: #666;
`;

const AuraScore = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: #444;
  white-space: nowrap;
  flex-shrink: 0;
`;

const DropdownHint = styled.div`
  padding: 0.5rem 1rem;
  font-size: 0.78rem;
  color: #888;
`;

const UserModeBanner = styled.div`
  padding: 0.35rem 1rem;
  font-size: 0.75rem;
  color: #1b4332;
  background: #88d8a3;
  border-radius: 4px 4px 0 0;
`;

function applyCategoryToParams(params, encodedCategory) {
  const { categoryValue, subCategory, config } = decodeSearchCategory(encodedCategory);
  // Clear previous category filters first
  params.delete('category');
  params.delete('subcategory');
  params.delete('book_subcategory');
  if (!categoryValue) return;
  params.set('category', categoryValue);
  if (subCategory) {
    params.set(config.subcategoryParam || 'search', subCategory);
  }
}

const SearchBar = ({ initialValue = '', onSearch }) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [category, setCategory] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = getVerticalThemeFromPath(location.pathname);
  const themeFg = SEARCH_FG[theme] || SEARCH_FG.mint;
  const darkTheme = isDarkVerticalSearchTheme(theme);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Sync with URL ?search= / ?q= / ?category= on listing & global search pages
  useEffect(() => {
    const onListingPage =
      location.pathname === '/pre-owned-items' ||
      location.pathname === '/items' ||
      location.pathname === '/defected-items' ||
      location.pathname === '/hobbies-finds';
    const onGlobalSearch = location.pathname === '/search';
    if (!onListingPage && !onGlobalSearch) return;
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('q') || params.get('search') || '';
    setSearchTerm(fromUrl);
    const cat = params.get('category') || '';
    setCategory(cat);
  }, [location.pathname, location.search]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isExplicitUserQuery = (value) => value.trimStart().startsWith('@');
  const userSearchQuery = (value) => {
    const t = value.trimStart();
    return t.startsWith('@') ? t.slice(1).trim() : value.trim();
  };

  const fetchAura = async (userId) => {
    try {
      const res = await axios.get(`${AURA_BASE}/api/aura/public/${userId}/`);
      return res.data;
    } catch {
      return null;
    }
  };

  const handleChange = useCallback((value) => {
    setSearchTerm(value);
    const query = userSearchQuery(value);
    const explicit = isExplicitUserQuery(value);

    if (query.length < 2) {
      setSuggestions([]);
      setDropdownOpen(false);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSuggestionsLoading(true);
      if (explicit) setDropdownOpen(true);
      try {
        const users = await authAPI.searchUsers(query);
        const withAura = await Promise.all(
          (users || []).map(async (u) => {
            const aura = await fetchAura(u.id);
            return { ...u, aura };
          })
        );
        setSuggestions(withAura);
        setDropdownOpen(explicit || withAura.length > 0);
      } catch {
        setSuggestions([]);
        setDropdownOpen(explicit);
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);
  }, []);

  const handleSelectUser = (username) => {
    setDropdownOpen(false);
    setSearchTerm('');
    navigate(`/u/${username}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = searchTerm.trim();
    const { categoryValue } = decodeSearchCategory(category);

    if (isExplicitUserQuery(trimmed)) {
      const username = userSearchQuery(trimmed);
      if (username) {
        setDropdownOpen(false);
        navigate(`/u/${username}`);
      }
      return;
    }

    const usernameQuery = userSearchQuery(trimmed);
    if (!categoryValue && /^[A-Za-z0-9_]{3,150}$/.test(usernameQuery)) {
      try {
        await authAPI.getPublicProfile(usernameQuery);
        navigate(`/u/${usernameQuery}`);
        return;
      } catch {
        // not a username, fall through to item search
      }
    }

    if (onSearch) {
      onSearch(searchTerm, categoryValue || '');
      return;
    }

    // Category only (no query) → browse that department like the sandwich menu
    if (categoryValue && !trimmed) {
      const params = new URLSearchParams();
      applyCategoryToParams(params, category);
      navigate(`/pre-owned-items?${params.toString()}`);
      return;
    }

    // Pre-owned / defected / hobbies browse pages already apply ?search= in-page
    const onScopedListing =
      location.pathname === '/pre-owned-items' ||
      location.pathname === '/items' ||
      location.pathname === '/defected-items' ||
      location.pathname === '/hobbies-finds';

    if (onScopedListing) {
      const params = new URLSearchParams(location.search);
      if (trimmed) {
        params.set('search', trimmed);
      } else {
        params.delete('search');
      }
      applyCategoryToParams(params, category);
      const qs = params.toString();
      navigate(qs ? `${location.pathname}?${qs}` : location.pathname);
      return;
    }

    // Home, luxury, designers, boutiques, wedding, etc. → all listing types
    const params = new URLSearchParams();
    if (trimmed) params.set('q', trimmed);
    if (categoryValue) params.set('category', categoryValue);
    const qs = params.toString();
    navigate(qs ? `/search?${qs}` : '/search');
  };

  const explicitUserQuery = isExplicitUserQuery(searchTerm);
  const query = userSearchQuery(searchTerm);
  const showDropdown =
    dropdownOpen &&
    (explicitUserQuery || suggestionsLoading || suggestions.length > 0);
  return (
    <SearchBarContainer $theme={theme}>
      <SearchBarContent>
        <SearchWrapper ref={wrapperRef}>
          <SearchForm $theme={theme} onSubmit={handleSubmit}>
            <SearchCategorySelect
              value={category}
              onChange={setCategory}
              styleVariant={darkTheme ? 'frameless' : 'framelessLight'}
              accentColor={themeFg.text}
            />
            <SearchInput
              $theme={theme}
              type="text"
              placeholder="Search for items or sellers…"
              value={searchTerm}
              onChange={(e) => handleChange(e.target.value)}
              onFocus={() => {
                if (query.length >= 2 && (explicitUserQuery || suggestions.length > 0)) {
                  setDropdownOpen(true);
                }
              }}
              autoComplete="off"
            />
            <SearchButton $theme={theme} type="submit" aria-label="Search">
              🔍
            </SearchButton>
          </SearchForm>

          {showDropdown && (
            <Dropdown role="listbox">
              <UserModeBanner>👤 Sellers</UserModeBanner>

              {suggestionsLoading && (
                <DropdownHint>Finding users…</DropdownHint>
              )}

              {!suggestionsLoading && query.length >= 2 && suggestions.length === 0 && explicitUserQuery && (
                <DropdownHint>No users found for "{query}"</DropdownHint>
              )}

              {suggestions.map((u) => (
                  <DropdownItem
                    key={u.id}
                    role="option"
                    onClick={() => handleSelectUser(u.username)}
                  >
                    <Avatar>
                      {u.profile_picture ? (
                        <AvatarImg src={u.profile_picture} alt="" />
                      ) : (
                        (u.name || u.username || '?')[0].toUpperCase()
                      )}
                    </Avatar>
                    <UserInfo>
                      <UserName>{u.name || u.username}</UserName>
                      <UserHandle>@{u.username}</UserHandle>
                    </UserInfo>
                    {u.aura && (
                      <AuraScore>⭐ {formatAuraScoreDisplay(u.aura.score)}</AuraScore>
                    )}
                  </DropdownItem>
              ))}
            </Dropdown>
          )}
        </SearchWrapper>
      </SearchBarContent>
    </SearchBarContainer>
  );
};

export default SearchBar;
